'use client'

import type { Schema } from 'yup'
import type {
    FailedPartialValidationEventDetail,
    PassedPartialValidationEventDetail,
} from '../../types/EventDetailTypes'
import { useTriggerValidation } from './hooks/useTriggerValidation'
import { useFormElement } from '../utility-hooks/UseFormElement'
import { validateSchema } from './helpers/validateSchema'
import { useCaptureEventsOnIndividualInputsAndMarkTargetAsInputWithCustomValidation } from './hooks/useCaptureEventsAndMarkTargetAsInputWithValidation'
import { useEventDispatchQueue } from '../UseEventDispatchQueue'
import { setCustomValidityForFields } from './helpers/setCustomValidityForFields'
import { getArrayOfValidatableInputsFromSource } from '../../helpers/getArrayOfValidatableInputsFromSource'

export interface UseValidateSchemaOptions {
    dispatchDelayForPartialValidationEvents?: number
    fieldsWithCustomValidation?: string[]
}
/**
 * @namespace
 * @description
 * Returns the means to validate an entire schema. Also sets up a listener on the document
 * to "request-partial-validation" events, that only validates fields related to that event.
 *
 * @listen RequestPartialValidation
 *
 * @param formId -- the id of the form this hook should validate on
 * @param schema -- the yup schema that should be used for validation
 * @param options.dispatchDelayForPartialValidationEvents -- Overrides the amount of milliseconds
 * that should be between dispatching of partial-validation-requests. A higher amount means
 * better performance but slower perceived performance. Default is 200 milliseconds.
 * @param options.relatedFields -- Used to group partial validations. If fieldNames are marked as
 * related to a fieldName, they are validated when together with the field on which validation
 * was requested.
 *
 * @returns object.validate -- validates the entire schema and returns the validation errors
 *
 * */
export const useValidateSchema = (formId: string, schema: Schema | null, options?: UseValidateSchemaOptions) => {
    const formElement = useFormElement(formId)
    const eventQueue = useEventDispatchQueue<PassedPartialValidationEventDetail | FailedPartialValidationEventDetail>()
    useTriggerValidation(schema, formElement, eventQueue, options?.dispatchDelayForPartialValidationEvents)
    useCaptureEventsOnIndividualInputsAndMarkTargetAsInputWithCustomValidation(
        formElement,
        options?.fieldsWithCustomValidation
    )
    return {
        validate: () => {
            if (!schema) return
            const inputs = getArrayOfValidatableInputsFromSource(formElement)
            const fieldNames = inputs?.map((input) => input.name)

            const validationMessages = validateSchema(formElement, schema)
            setCustomValidityForFields(fieldNames ?? [], formElement, validationMessages)

            return validationMessages
        },
    }
}
